import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { OpenIdConnectSelector, OpenIdConnectState } from "@obg/common.verification";
import { GtmLoginMethod, GtmRegistrationType } from "@obg/gtm.types/adaptive";
import { ActionWithPayload } from "@obg/ngrx-utils";
import { CommonState } from "@obg/ngx.common";
import { DGA_AUTH_TRANSACTION_ACTION_TYPE, DgaAuthTransactionSelector } from "@obg/ngx.core";
import { TelemetryService } from "@obg/ngx.global.evo";
import { differenceInYears } from "date-fns";
import { head } from "lodash-es";
import { filter, map, tap, withLatestFrom } from "rxjs/operators";

import { DgaRegistrationSelector } from "../registration/dga/dga-registration.selector";
import { REGISTRATION_ACTION_TYPE } from "../registration/registration.action";
import { RegistrationService } from "../registration/registration.service";
import { RegistrationFeatureState } from "../state";

import { RegistrationTelemetryEventName, TelemetryErrorPayload } from "./registration-telemetry.model";
import { getErrorMessage } from "./registration-telemetry.util";

@Injectable()
export class DgaRegistrationTelemetryEffect {
	registrationFail$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(DGA_AUTH_TRANSACTION_ACTION_TYPE.initializeAuthTransactionFail),
				map((x: ActionWithPayload<TelemetryErrorPayload>) => x.payload),
				withLatestFrom(this.store.select(this.oidcSelector.getToken())),
				tap(([error]) =>
					this.telemetryService.track({
						event: RegistrationTelemetryEventName.FailedRegistration,
						Error: getErrorMessage(error),
						RegistrationType: GtmLoginMethod.MitID,
					})
				)
			),
		{ dispatch: false }
	);

	dgaRegistrationSuccess$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(REGISTRATION_ACTION_TYPE.registrationSuccess),
				withLatestFrom(
					this.store.select(this.dgaAuthTransactionSelector.getExperianData()),
					this.store.select(this.dgaRegistrationSelector.getState()),
					this.store.select(this.oidcSelector.getToken())
				),
				filter(([, experianData, registrationState]) => !!(experianData && registrationState?.token)),
				tap(([, experianData, registrationState]) => {
					const affiliateData = head(this.registrationService.getAffiliateMetaData())!;
					this.telemetryService.track({
						event: RegistrationTelemetryEventName.ConfirmedRegistration,
						HashedEmail: registrationState.hashedEmail || "Missing",
						GUID: registrationState.token,
						Age: differenceInYears(Date.now(), new Date(experianData!.dateOfBirth)).toString(),
						Sex: experianData!.gender,
						Location: "Denmark",
						PartnerID: affiliateData?.affiliate,
						RegistrationType: GtmLoginMethod.MitID,
						RegistrationMethod: GtmRegistrationType.Regular,
					});
				})
			),
		{ dispatch: false }
	);

	constructor(
		private actions$: Actions,
		private telemetryService: TelemetryService,
		private store: Store<RegistrationFeatureState & CommonState & OpenIdConnectState>,
		private dgaAuthTransactionSelector: DgaAuthTransactionSelector,
		private registrationService: RegistrationService,
		private dgaRegistrationSelector: DgaRegistrationSelector,
		private oidcSelector: OpenIdConnectSelector
	) {}
}
